import React, { useState, useRef } from 'react';
import { Upload, Download, CheckCircle, AlertTriangle, XCircle, FileSpreadsheet, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const MODULE_LABELS = {
  itr: 'ITR Filings',
  'tds-returns': 'TDS Returns',
  'tds-payments': 'TDS Payments',
  gst: 'GST Returns',
  audit: 'Audit Records',
  roc: 'ROC Compliance',
  esipf: 'ESI/PF Monthly',
};

export default function ModuleImport({ module: mod, onComplete, onClose }) {
  const [step, setStep] = useState(1); // 1=upload, 2=preview, 3=result
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const downloadTemplate = async () => {
    try {
      const resp = await api.get(`/compliance-import/template/${mod}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url; a.download = `KUA_${mod}_Template.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    setLoading(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post(`/compliance-import/preview/${mod}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed to parse file'); }
    finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post(`/compliance-import/${mod}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      setStep(3);
      if (r.data.imported > 0) onComplete?.();
    } catch (e) { toast.error(e?.response?.data?.error || 'Import failed'); }
    finally { setLoading(false); }
  };

  const downloadErrors = () => {
    if (!result?.errors?.length) return;
    const csv = 'Row,Reason\n' + result.errors.map(e => `${e.rowNum},"${e.reason}"`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `${mod}_import_errors.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const rowStatusInfo = (row) => {
    if (row.errors?.length) return { label: 'Error', cls: 'bg-red-50 border-l-2 border-red-400', badge: 'text-red-600 bg-red-50', icon: <XCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" /> };
    if (row.warnings?.length) return { label: 'Warning', cls: 'bg-orange-50 border-l-2 border-orange-300', badge: 'text-orange-600 bg-orange-50', icon: <AlertTriangle size={14} className="text-orange-500 flex-shrink-0 mt-0.5" /> };
    return { label: 'Valid', cls: 'bg-green-50 border-l-2 border-green-300', badge: 'text-green-700 bg-green-50', icon: <CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" /> };
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Import {MODULE_LABELS[mod] || mod}</h2>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <span className={step >= 1 ? 'text-[#1e3a5f] font-medium' : ''}>1. Upload</span>
              <ChevronRight size={12} />
              <span className={step >= 2 ? 'text-[#1e3a5f] font-medium' : ''}>2. Preview</span>
              <ChevronRight size={12} />
              <span className={step >= 3 ? 'text-[#1e3a5f] font-medium' : ''}>3. Done</span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* STEP 1: Upload */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
                <strong>Format guide:</strong> Download the template below, fill in data, then upload. Client Code must match an existing client's code exactly. Dates in DD/MM/YYYY format.
              </div>
              <button onClick={downloadTemplate} className="flex items-center gap-2 px-4 py-2 border border-[#1e3a5f] text-[#1e3a5f] rounded-lg text-sm hover:bg-blue-50 transition-colors font-medium">
                <Download size={15} /> Download Template (.xlsx)
              </button>
              <div
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${dragging ? 'border-[#1e3a5f] bg-blue-50' : 'border-gray-300 hover:border-[#1e3a5f]'}`}
                onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onClick={() => fileRef.current?.click()}
              >
                <FileSpreadsheet size={36} className="mx-auto text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-600">Drag & drop your Excel / CSV file here</p>
                <p className="text-xs text-gray-400 mt-1">Supports .xlsx, .xls, .csv — max 10 MB</p>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e => handleFile(e.target.files[0])} />
              </div>
              {loading && <div className="text-center text-sm text-gray-400 py-4">Parsing file…</div>}
            </div>
          )}

          {/* STEP 2: Preview */}
          {step === 2 && preview && (
            <div className="space-y-4">
              <div className="flex gap-3 flex-wrap">
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle size={12} /> {preview.rows.filter(r => r.valid && !r.duplicate).length} Ready to import</span>
                {preview.rows.filter(r => r.warnings?.length).length > 0 && <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700"><AlertTriangle size={12} /> {preview.rows.filter(r => r.warnings?.length).length} Warnings</span>}
                {preview.rows.filter(r => r.errors?.length).length > 0 && <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><XCircle size={12} /> {preview.rows.filter(r => r.errors?.length).length} Errors (will skip)</span>}
              </div>
              <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
                {preview.rows.map((row, i) => {
                  const si = rowStatusInfo(row);
                  return (
                    <div key={i} className={`rounded-lg p-3 ${si.cls}`}>
                      <div className="flex items-start gap-2">
                        {si.icon}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap gap-2 items-center">
                            <span className="font-mono text-xs text-gray-500">Row {row.rowNum}</span>
                            <span className="text-sm font-medium text-gray-800 truncate">{row.clientName || row.code || '(no client)'}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${si.badge}`}>{si.label}</span>
                          </div>
                          {row.errors?.length > 0 && <ul className="mt-1 space-y-0.5">{row.errors.map((e, j) => <li key={j} className="text-xs text-red-600">{e}</li>)}</ul>}
                          {row.warnings?.length > 0 && <ul className="mt-1 space-y-0.5">{row.warnings.map((w, j) => <li key={j} className="text-xs text-orange-600">{w}</li>)}</ul>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-3 pt-2 border-t">
                <button onClick={() => { setStep(1); setPreview(null); setFile(null); }} className="btn-secondary flex-1">Back</button>
                <button onClick={handleImport} disabled={loading || preview.valid === 0} className="btn-primary flex-1 disabled:opacity-50">
                  {loading ? 'Importing…' : `Import ${preview.valid} Records`}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Result */}
          {step === 3 && result && (
            <div className="space-y-4 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${result.imported > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                {result.imported > 0 ? <CheckCircle size={32} className="text-green-600" /> : <XCircle size={32} className="text-red-600" />}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{result.imported > 0 ? 'Import Complete' : 'Nothing imported'}</h3>
                <p className="text-sm text-gray-500 mt-1">{result.imported} of {result.total} records imported successfully</p>
              </div>
              {result.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-left">
                  <p className="text-sm font-medium text-red-700 mb-2">{result.errors.length} rows failed:</p>
                  <ul className="space-y-1 max-h-32 overflow-y-auto">
                    {result.errors.map((e, i) => <li key={i} className="text-xs text-red-600">Row {e.rowNum}: {e.reason}</li>)}
                  </ul>
                  <button onClick={downloadErrors} className="mt-2 text-xs text-red-700 underline hover:no-underline">Download error report (.csv)</button>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => { setStep(1); setFile(null); setPreview(null); setResult(null); }} className="btn-secondary flex-1">Import More</button>
                <button onClick={onClose} className="btn-primary flex-1">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
